"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cog, Fan, Radio, ShieldCheck, SlidersHorizontal, Zap } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const componentItems = [{
  id: "system-01",
  code: "SYS-01",
  title: "Controllers & Control Boxes",
  description: "Browse available operator controllers, control boxes and related control components. Always compare the existing controller model, connections and documented machine compatibility before replacing one.",
  icon: SlidersHorizontal
}, {
  id: "system-02",
  code: "SYS-02",
  title: "Inflatable Safety Arenas",
  description: "Replacement inflatable arenas and inflatable components need to match the intended mechanical bull setup. Check the actual dimensions, attachment points, blower requirements and product information before ordering.",
  icon: ShieldCheck
}, {
  id: "system-03",
  code: "SYS-03",
  title: "Blowers",
  description: "The blower must suit the inflatable it is intended to support. Confirm the requirements shown for the exact inflatable rather than assuming any blower will work.",
  icon: Fan
}, {
  id: "system-04",
  code: "SYS-04",
  title: "Switches & Electrical Components",
  description: "Available switches, emergency-stop components and electrical replacement items should be matched against the existing system before installation.",
  icon: Zap
}, {
  id: "system-05",
  code: "SYS-05",
  title: "Mechanical Components",
  description: "Motors, couplings, bearings, gearboxes and related mechanical parts can be highly model-specific.",
  icon: Cog
}, {
  id: "system-06",
  code: "SYS-06",
  title: "Covers, Remotes & Accessories",
  description: "Smaller accessories can still have compatibility requirements. Review dimensions, connection type and product notes before enquiring.",
  icon: Radio
}];
export default function ComponentParts() {
  const prefersReducedMotion = useReducedMotion();
  return <section id="partsbycomponent" data-nav="light" className="w-full bg-background py-16 sm:py-20 md:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="h-2 w-2 bg-primary rounded-none" aria-hidden="true" />
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              Equipment Architecture Breakdown
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground leading-none">
            Find Parts by Component
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A mechanical bull is made up of several separate systems, so the part you need depends on where the problem or replacement requirement occurs. Start with the component category, then use the individual product information to narrow the match.
          </p>
        </div>

        {/* 6-Card System Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {componentItems.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={i} initial={prefersReducedMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-40px"
          }} transition={{
            duration: 0.3,
            ease: [0.16, 1, 0.3, 1],
            delay: prefersReducedMotion ? 0 : i * 0.05
          }} className="h-full">
                <Card className="h-full rounded-none border border-border bg-card transition-colors duration-200 hover:border-primary flex flex-col justify-between py-6 px-0 shadow-none">
                  <CardHeader className="gap-4 px-6 pb-2">
                    <div className="flex items-center justify-between border-b border-border/60 pb-4">
                      <div className="p-2.5 bg-muted rounded-none border border-border inline-flex items-center justify-center text-primary">
                        <IconComponent className="size-5" aria-hidden="true" />
                      </div>
                      <span className="font-mono text-xs font-medium tracking-wider text-muted-foreground">
                        {item.code}
                      </span>
                    </div>

                    <CardTitle className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-normal text-card-foreground mt-2">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pt-2">
                    <p className="text-sm leading-relaxed text-muted-foreground font-sans">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}